import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { formsService } from '../../../services/formsService';
import { abTestingService } from '../../../services/abTestingService';

const CreateTestModal = ({ onClose, onCreated }) => {
  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testName, setTestName] = useState('');
  const [description, setDescription] = useState('');
  const [formAId, setFormAId] = useState('');
  const [formBId, setFormBId] = useState('');
  const [trafficA, setTrafficA] = useState(50);

  useEffect(() => {
    const loadForms = async () => {
      try {
        const allForms = await formsService.getForms();
        setForms((allForms || []).filter((form) => form?.published));
      } catch (err) {
        setError(err?.message || 'Could not load your published forms.');
      } finally {
        setLoadingForms(false);
      }
    };
    loadForms();
  }, []);

  const hasEnoughForms = forms.length >= 2;
  const formOptions = useMemo(() => forms.map((form) => ({
    id: form.id,
    label: form.title || form.name || form.id,
  })), [forms]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      setSaving(true);
      const test = await abTestingService.createFormTest({
        testName,
        description,
        formAId,
        formBId,
        trafficA,
      });
      onCreated(test);
    } catch (err) {
      setError(err?.message || 'Could not create the experiment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-card shadow-xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card p-6">
          <div>
            <h2 className="text-xl font-bold text-foreground">Create form A/B test</h2>
            <p className="mt-1 text-sm text-muted-foreground">Compare two published forms through one shareable link.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-muted" aria-label="Close">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="space-y-5 p-6">
          {error && (
            <div className="flex gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <Icon name="CircleAlert" size={18} />
              <p>{error}</p>
            </div>
          )}

          <Input
            label="Experiment name"
            value={testName}
            onChange={(event) => setTestName(event.target.value)}
            placeholder="e.g. Lead form headline test"
            required
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="experiment-description">Description <span className="text-muted-foreground">(optional)</span></label>
            <textarea
              id="experiment-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What are you trying to learn?"
              className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {loadingForms ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Icon name="Loader2" size={18} className="mr-2 animate-spin" />
              Loading published forms…
            </div>
          ) : !hasEnoughForms ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              Publish at least two forms before creating an A/B test.
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" htmlFor="form-a">Form A <span className="text-muted-foreground">(control)</span></label>
                  <select
                    id="form-a"
                    value={formAId}
                    onChange={(event) => setFormAId(event.target.value)}
                    required
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Choose a published form</option>
                    {formOptions.map((form) => <option key={form.id} value={form.id}>{form.label}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground" htmlFor="form-b">Form B</label>
                  <select
                    id="form-b"
                    value={formBId}
                    onChange={(event) => setFormBId(event.target.value)}
                    required
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Choose a published form</option>
                    {formOptions.map((form) => <option key={form.id} value={form.id} disabled={form.id === formAId}>{form.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Traffic split</p>
                    <p className="text-xs text-muted-foreground">Returning browsers keep the same assigned form.</p>
                  </div>
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">{trafficA}% / {100 - trafficA}%</span>
                </div>
                <input
                  aria-label="Traffic split for Form A"
                  type="range"
                  min="1"
                  max="99"
                  value={trafficA}
                  onChange={(event) => setTrafficA(Number(event.target.value))}
                  className="w-full accent-primary"
                />
                <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                  <span>Form A</span><span>Form B</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-border p-6">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving || loadingForms || !hasEnoughForms}>
            {saving && <Icon name="Loader2" size={16} className="animate-spin" />}
            Create experiment
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateTestModal;